import { Avatar, Box, Button, Menu, MenuItem, TextField, Typography, useTheme } from '@mui/material'
import { ChangeEvent, useState } from 'react'

import { IAccountByIdRawRespDto } from 'src/types'
import { AccountSocials } from './account-socials.component'
import { truncateString } from 'src/common/utils'
import { useTranslation } from 'react-i18next'

interface IProps {
  accounts: IAccountByIdRawRespDto[]
  handleAccountSelect: (account: IAccountByIdRawRespDto) => void
  selectedAccount: IAccountByIdRawRespDto | null
}

export const AccountsSelection = ({ accounts, handleAccountSelect, selectedAccount }: IProps) => {
  const [t] = useTranslation()
  const theme = useTheme()

  const [searchQuery, setSearchQuery] = useState<string>('')

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null)

  const handleCloseMenu = (): void => {
    setAnchorEl(null)
    setSearchQuery('')
  }

  const handleOpenMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleSelect = (account: IAccountByIdRawRespDto) => {
    handleAccountSelect(account)
    setAnchorEl(null)
    setSearchQuery('')
  }

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value)
  }

  const filteredAccounts = accounts.filter((account: IAccountByIdRawRespDto) => {
    const searchString = account.account_holder.toLowerCase()
    return searchString.includes(searchQuery.toLowerCase())
  })

  return (
    <Box sx={{ width: '100%' }}>
      <Button
        variant="outlined"
        onClick={handleOpenMenu}
        sx={{
          textTransform: 'none',
          color: theme.palette.custom.darkBlue2,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'start',
        }}
      >
        {selectedAccount ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              gap: '3px',
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'start',
                gap: '15px',
                width: '100%',
              }}
            >
              <Avatar
                sx={{ width: 30, height: 30 }}
                alt={selectedAccount.account_holder}
                src={selectedAccount.image_small || ''}
              />
              <Typography variant="body1">{selectedAccount.account_holder}</Typography>
            </Box>
            {selectedAccount.website && (
              <Typography
                sx={{
                  textAlign: 'left',
                  width: '100%',
                  fontSize: '12px',
                  color: theme.palette.custom.grey2,
                }}
              >
                {truncateString(selectedAccount.website, 50)}
              </Typography>
            )}
          </Box>
        ) : (
          <Typography sx={{ color: theme.palette.custom.grey2 }}>
            Select Existing Account
          </Typography>
        )}
      </Button>
      {selectedAccount?.socials && <AccountSocials socials={selectedAccount.socials} />}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        id="select-existing-account"
        onClose={handleCloseMenu}
      >
        <TextField
          onKeyDown={(e) => {
            if (e.key !== 'Escape') {
              e.stopPropagation()
            }
          }}
          value={searchQuery}
          onChange={handleSearchChange}
          variant={'standard'}
          sx={{ width: '100%', p: '6px 16px' }}
          placeholder={t('search-input-placeholder.v')}
          autoFocus
        />
        {filteredAccounts.map((item) => {
          return (
            <MenuItem
              key={item.id}
              onClick={() => handleSelect(item)}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                gap: '3px',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'start',
                  gap: '15px',
                  width: '100%',
                }}
              >
                <Avatar
                  style={{ width: 30, height: 30 }}
                  alt={item.account_holder}
                  src={item.image_small || item.image}
                />
                {/* <Icon icon={`flag:${country.code.toLowerCase()}-4x3`} /> */}

                <Typography sx={{ fontSize: '14px' }}>
                  {truncateString(item.account_holder, 40)}
                </Typography>
              </Box>
              {item.website && (
                <Typography
                  sx={{
                    textAlign: 'left',
                    width: '100%',
                    fontSize: '12px',
                    color: theme.palette.custom.grey2,
                  }}
                >
                  {truncateString(item.website, 50)}
                </Typography>
              )}
            </MenuItem>
          )
        })}
      </Menu>
    </Box>
  )
}
